import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { ApiError } from "@/src/libs/ApiError";

/**
 * La fiche d'administration d'un événement.
 *
 * ⚠️ **`GET /moderation/events/{id}`, et surtout pas `GET /event/{id}`.** Les
 * deux rendent le même événement, mais la route publique **masque l'adresse**
 * d'un événement privé y compris pour un administrateur — le serveur n'y fait
 * aucune exception de rôle, c'est vérifié par un test de son côté. L'adresse et
 * les coordonnées ne sortent que par la route d'administration.
 *
 * `distance` y vaut toujours `null` : une fiche ne part d'aucun centre de
 * recherche. Le champ n'a de valeur que dans la liste, et seulement quand un
 * lieu y est filtré.
 */
export function useEvent(eventId: number) {
  const $api = useApiClient();
  const t = useTranslations("events");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/events/{id}",
    { params: { path: { id: eventId } } },
    // ⚠️ **Un identifiant illisible ne part pas au serveur.** `/events/abc`
    // donne `NaN`, que l'écran ramène à `0` : la route le refuserait en 400
    // `INVALID_REQUEST_DATA`, ce qui ferait surgir un toast de panne sur ce
    // qui n'est qu'une URL mal tapée.
    { enabled: eventId > 0 },
  );

  // Un 404 est un cas normal : l'URL a été tapée à la main, ou l'événement a
  // été supprimé depuis. L'écran le dit lui-même ; un toast ferait croire à une
  // panne du serveur.
  const isMissing = ApiError.from(error)?.status === 404;

  useQueryErrorToast(isError, t("detail-error"), error, !isMissing);

  return { event: data?.data, isLoading, isError, isMissing };
}
